/**
 * useBuchhaltung — Hooks für Eingangsbelege, Buchungsvorschläge, Überweisungen
 *
 * ROBUST: Wenn Tabellen nicht existieren (PGRST205), wird ein leeres Array
 * zurückgegeben statt einer Fehlermeldung.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type {
  Eingangsbeleg,
  Buchungsvorschlag,
  Ueberweisung,
  FirmaId,
  EingangsbelegStatus,
  UeberweisungStatus,
} from '@/lib/database.types';
import { localIsoDate } from '@/lib/date-utils';

// ============================================================
// Generic useQuery (same pattern as useSupabase.ts)
// ============================================================

function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return msg.includes('PGRST205') || msg.includes('schema cache') || msg.includes('relation') || msg.includes('does not exist');
}

function useQuery<T>(fetcher: () => Promise<T>, deps: any[] = [], fallback?: T) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err: any) {
      if (isTableNotFound(err) && fallback !== undefined) {
        setData(fallback);
        setError(null);
      } else {
        setError(err.message || 'Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

// ============================================================
// EINGANGSBELEGE
// ============================================================

export function useEingangsbelege(firma?: FirmaId, status?: EingangsbelegStatus) {
  return useQuery(async () => {
    let query = supabase
      .from('eingangsbelege')
      .select('*')
      .order('eingangsdatum', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(500);

    if (firma) query = query.eq('firma', firma);
    if (status) query = query.eq('status', status);

    const { data, error } = await query;
    if (error) throw error;
    return data as Eingangsbeleg[];
  }, [firma, status], [] as Eingangsbeleg[]);
}

export async function createEingangsbeleg(beleg: Partial<Eingangsbeleg>) {
  const { data, error } = await supabase
    .from('eingangsbelege')
    .insert(beleg as any)
    .select()
    .single();
  if (error) throw error;
  return data as Eingangsbeleg;
}

export async function updateEingangsbeleg(id: string, updates: Partial<Eingangsbeleg>) {
  const { data, error } = await supabase
    .from('eingangsbelege')
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Eingangsbeleg;
}

// ============================================================
// BUCHUNGSVORSCHLÄGE
// ============================================================

export function useBuchungsvorschlaege(firma?: FirmaId) {
  return useQuery(async () => {
    let query = supabase
      .from('buchungsvorschlaege')
      .select('*, eingangsbelege(*)')
      .order('created_at', { ascending: false })
      .limit(500);

    if (firma) query = query.eq('firma', firma);

    const { data, error } = await query;
    if (error) throw error;
    return data as Buchungsvorschlag[];
  }, [firma], [] as Buchungsvorschlag[]);
}

export async function updateBuchungsvorschlag(id: string, updates: Partial<Buchungsvorschlag>) {
  const { data, error } = await supabase
    .from('buchungsvorschlaege')
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Buchungsvorschlag;
}

// ============================================================
// ÜBERWEISUNGEN
// ============================================================

export function useUeberweisungen(firma?: FirmaId, status?: UeberweisungStatus) {
  return useQuery(async () => {
    let query = supabase
      .from('ueberweisungen')
      .select('*, eingangsbelege(*)')
      .order('created_at', { ascending: false })
      .limit(500);

    if (firma) query = query.eq('firma', firma);
    if (status) query = query.eq('status', status);

    const { data, error } = await query;
    if (error) throw error;
    return data as Ueberweisung[];
  }, [firma, status], [] as Ueberweisung[]);
}

export async function createUeberweisung(ueberweisung: Partial<Ueberweisung>) {
  const { data, error } = await supabase
    .from('ueberweisungen')
    .insert(ueberweisung as any)
    .select()
    .single();
  if (error) throw error;
  return data as Ueberweisung;
}

export async function updateUeberweisung(id: string, updates: Partial<Ueberweisung>) {
  const { data, error } = await supabase
    .from('ueberweisungen')
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Ueberweisung;
}

// ============================================================
// BWA (Betriebswirtschaftliche Auswertung)
// ============================================================

export function useBWA(firma: FirmaId, zeitraumVon?: string, zeitraumBis?: string) {
  return useQuery(async () => {
    const von = zeitraumVon || localIsoDate(new Date(new Date().getFullYear(), 0, 1));
    const bis = zeitraumBis || localIsoDate(new Date());

    // Einnahmen: bezahlte Rechnungen im Zeitraum
    const { data: rechnungen, error: rErr } = await supabase
      .from('rechnungen')
      .select('brutto_betrag, netto_betrag, mwst_betrag, status, bezahlt_am, business_unit')
      .gte('rechnungsdatum', von)
      .lte('rechnungsdatum', bis);
    if (rErr && !isTableNotFound(rErr)) throw rErr;

    // Ausgaben: eingangsbelege im Zeitraum
    const { data: belege, error: bErr } = await supabase
      .from('eingangsbelege')
      .select('brutto_betrag, netto_betrag, mwst_betrag, typ, status, firma')
      .eq('firma', firma)
      .gte('belegdatum', von)
      .lte('belegdatum', bis);
    if (bErr && !isTableNotFound(bErr)) throw bErr;

    const einnahmenBrutto = (rechnungen || [])
      .filter(r => r.status === 'bezahlt')
      .reduce((s, r) => s + (r.brutto_betrag || 0), 0);
    const einnahmenNetto = (rechnungen || [])
      .filter(r => r.status === 'bezahlt')
      .reduce((s, r) => s + (r.netto_betrag || 0), 0);
    const ausgabenBrutto = (belege || [])
      .filter(b => !['abgelehnt'].includes(b.status))
      .reduce((s, b) => s + (b.brutto_betrag || 0), 0);
    const ausgabenNetto = (belege || [])
      .filter(b => !['abgelehnt'].includes(b.status))
      .reduce((s, b) => s + (b.netto_betrag || 0), 0);

    return {
      zeitraum: { von, bis },
      umsatzerloese: einnahmenNetto,
      umsatzerloeseBrutto: einnahmenBrutto,
      aufwendungen: ausgabenNetto,
      aufwendungenBrutto: ausgabenBrutto,
      betriebsergebnis: einnahmenNetto - ausgabenNetto,
      vorsteuer: (belege || []).reduce((s, b) => s + (b.mwst_betrag || 0), 0),
      umsatzsteuer: (rechnungen || []).filter(r => r.status === 'bezahlt').reduce((s, r) => s + (r.mwst_betrag || 0), 0),
      rechnungenGesamt: (rechnungen || []).length,
      belegeGesamt: (belege || []).length,
    };
  }, [firma, zeitraumVon, zeitraumBis], {
    zeitraum: { von: '', bis: '' },
    umsatzerloese: 0,
    umsatzerloeseBrutto: 0,
    aufwendungen: 0,
    aufwendungenBrutto: 0,
    betriebsergebnis: 0,
    vorsteuer: 0,
    umsatzsteuer: 0,
    rechnungenGesamt: 0,
    belegeGesamt: 0,
  });
}
